import clsx from 'clsx'
import { useIsMobile } from 'hooks/useIsMobile'
import { MouseEvent, useState } from 'react'
import { ArrowContainer, Popover } from 'react-tiny-popover'
import { STYLES } from 'utils/constants'

import style from './OverlayControl.module.css'

export interface IOverlayItem {
  onClick: () => void
  active: boolean
  disabled: boolean
  content: React.ReactNode
}

const PopoverContent = ({
  mapStyle,
  onStyleChanged,
  overlayItems,
  closePopover,
}: {
  mapStyle: string
  onStyleChanged: () => void
  overlayItems?: IOverlayItem[]
  closePopover: () => void
}) => {
  const onVectorStyleClick = () => {
    if (mapStyle === STYLES.SATELLITE) {
      onStyleChanged()
    }
    closePopover()
  }
  const onSatelliteStyleClick = () => {
    if (mapStyle !== STYLES.SATELLITE) {
      onStyleChanged()
    }
    closePopover()
  }

  const isLaserscan = (content: React.ReactNode): boolean => {
    if (!content) return false
    if (typeof content === 'string') return content.toLowerCase().includes('laserscan')

    const props = (content as any).props
    if (props && props.children) {
      if (typeof props.children === 'string') {
        return props.children.toLowerCase().includes('laserscan')
      }
      if (Array.isArray(props.children)) {
        return props.children.some(
          (child: any) => typeof child === 'string' && child.toLowerCase().includes('laserscan')
        )
      }
    }
    return false
  }

  return (
    <div className={style.popover}>
      <div
        className={clsx(style.popoverItem, { [style.popoverItemActive]: mapStyle !== STYLES.SATELLITE })}
        onClick={onVectorStyleClick}
      >
        Map
      </div>
      <div
        className={clsx(style.popoverItem, { [style.popoverItemActive]: mapStyle === STYLES.SATELLITE })}
        onClick={onSatelliteStyleClick}
      >
        Satellite
      </div>
      <div className={style.popoverSeparator}></div>
      {overlayItems?.map((item, index) => (
        <div
          key={index}
          onClick={() => {
            if (!item.disabled) {
              item.onClick()
              if (isLaserscan(item.content)) {
                closePopover()
              }
            }
          }}
          className={clsx(style.popoverItem, {
            [style.popoverItemActive]: item.active && !item.disabled,
            [style.popoverItemDisabled]: item.disabled,
          })}
        >
          {item.content}
        </div>
      ))}
    </div>
  )
}

const OverlayControl = ({
  mapStyle,
  onStyleChanged,
  overlayItems,
  right,
}: {
  mapStyle: string
  onStyleChanged: () => void
  overlayItems?: IOverlayItem[]
  right?: number
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const isMobile = useIsMobile()

  const onClickOutside = () => {
    setIsPopoverOpen(false)
  }

  const onClick = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsPopoverOpen(prev => !prev)
  }

  return (
    <Popover
      onClickOutside={onClickOutside}
      isOpen={isPopoverOpen}
      positions={['left', 'top', 'bottom', 'right']} // preferred positions by priority
      reposition={true}
      containerStyle={{ zIndex: '2001' }}
      parentElement={(document.fullscreenElement as HTMLElement) || document.body}
      content={() => (
        <PopoverContent
          mapStyle={mapStyle}
          onStyleChanged={onStyleChanged}
          overlayItems={overlayItems}
          closePopover={() => setIsPopoverOpen(false)}
        />
      )}
    >
      <div className={style.root} style={isMobile && right !== undefined ? { right } : {}}>
        <button className={style.button} type="button" onClick={onClick}>
          <span className={clsx('mapboxgl-ctrl-icon', style.icon)} title="Layers" />
        </button>
      </div>
    </Popover>
  )
}

export default OverlayControl
